import { type ChangeEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormikErrors } from 'formik';
import { Alert, Divider, Snackbar, Stack } from '@mui/material';

import type {
  Document,
  TaskTodo,
  TaskTodoCreateOptions,
  Option,
  EntityRef,
} from 'models';
import { toDate, customFormat } from 'utils';
import { check, Warning } from './helpers';

import { FileLoader } from 'containers';

import { Chat, EntityName } from 'features/chat';

import ControlLabel from 'shared/components/ControlLabel';
import Textarea from 'shared/components/Textarea';
import {
  Modal,
  PageLoader,
  Error,
  Select,
  ValidationWarning,
  Input,
  DatePicker,
  NoData,
  SaveConfirm,
} from 'components';
import { ControlWrapper } from 'components/styled-components';
import ExpandIcon from 'shared/icons/Expand';
import ExpandedForm from '../ExpandedForm';

import {
  useStyles,
  StyledIconButton,
  SaveButton,
  CloseButton,
  CancelButton,
  CompleteButton,
  ReopenButton,
  ButtonLoader,
} from '../style';

interface Props {
  documents: Document[];
  taskId: string | null;
  options: TaskTodoCreateOptions | null;
  warningState: {
    open: boolean;
    type: 'cancel' | 'complete' | null;
  };
  values: TaskTodo;
  loading: boolean;
  error: boolean;
  validationErrors: FormikErrors<TaskTodo>;
  confirmationWarning: boolean;
  saveAndCopyUpload: boolean;
  upload: boolean;
  closeWithoutSaving: () => void;
  closeWithSaving: () => void;
  handleInitErrors: () => void;
  handleCloseTask: () => void;
  handleInitWarningType: () => void;
  handleReopenTask: () => Promise<void>;
  handleCancelTask: () => Promise<void>;
  handleCloseSubtaskWarning: () => void;
  handleCompleteTask: () => Promise<void>;
  handleChangeDate: (date: Date | null) => void;
  handleChangeInput: (e: ChangeEvent<HTMLInputElement>) => void;
  handleChangeSelect: (value: Option | EntityRef, name?: string) => void;
  handleChangeTextArea: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  handleSaveAndCopy: () => void;
  onChangeDescription: (value: string) => void;
  onChangeResolutionDescription: (value: string) => void;
  touchDocuments: () => void;
}

const TodoEditForm = ({
  documents,
  taskId,
  values,
  options,
  loading,
  error,
  validationErrors,
  confirmationWarning,
  upload,
  saveAndCopyUpload,
  closeWithSaving,
  closeWithoutSaving,
  warningState,
  handleInitWarningType,
  handleReopenTask,
  handleCloseTask,
  handleCompleteTask,
  handleCloseSubtaskWarning,
  handleChangeDate,
  handleChangeInput,
  handleChangeSelect,
  handleChangeTextArea,
  handleCancelTask,
  handleInitErrors,
  handleSaveAndCopy,
  onChangeDescription,
  onChangeResolutionDescription,
  touchDocuments,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [isShowedExpandedDescription, setIsShowedExpandedDescription] =
    useState(false);
  const [
    isShowedExpandedResolutionDescription,
    setIsShowedExpandedResolutionDescription,
  ] = useState(false);

  const selectCls = useMemo(
    () => ({
      root: classes.rootSelect,
      paper: {
        root: classes.paper,
      },
      select: classes.select,
      btn: {
        root: classes.selectBtn,
      },
    }),
    [classes]
  );

  const datePickerCls = useMemo(
    () => ({
      output: {
        root: classes.datePickerRoot,
      },
    }),
    [classes]
  );

  const handleOpenExpandedDescription = () => {
    setIsShowedExpandedDescription(true);
  };
  const handleCloseExpandedDescription = () => {
    setIsShowedExpandedDescription(false);
  };
  const handleOpenExpandedResolutionDescription = () => {
    setIsShowedExpandedResolutionDescription(true);
  };
  const handleCloseExpandedResolutionDescription = () => {
    setIsShowedExpandedResolutionDescription(false);
  };

  if (loading) return <PageLoader />;
  if (error) return <Error />;
  if (!options || !values) return <NoData />;

  const isOwner = !!values.IsOwner;
  const isExecutor = !!values.IsExecutor;
  const notOwnerNorExecutor = !(isOwner || isExecutor);
  const disabled = upload || saveAndCopyUpload;
  const isCompletedOrCanceledTask = check(values.State?.Value);
  const isDisabledField = notOwnerNorExecutor || isCompletedOrCanceledTask;

  const SaveBtn = (
    <ControlWrapper className={classes.btnControlWrapper}>
      <SaveButton
        onClick={closeWithSaving}
        disabled={isCompletedOrCanceledTask || disabled}
      >
        {t('#portal.task.todo.form.button.label.save')}
        {upload && <ButtonLoader />}
      </SaveButton>
    </ControlWrapper>
  );
  const SaveAndCopyBtn = (
    <ControlWrapper className={classes.btnControlWrapper}>
      <SaveButton
        onClick={handleSaveAndCopy}
        disabled={isCompletedOrCanceledTask || disabled}
      >
        Tallenna ja kopioi
        {saveAndCopyUpload && <ButtonLoader />}
      </SaveButton>
    </ControlWrapper>
  );
  const CloseBtn = (
    <ControlWrapper className={classes.btnControlWrapper}>
      <CloseButton
        onClick={
          isCompletedOrCanceledTask ? closeWithoutSaving : handleCloseTask
        }
        disabled={disabled}
      >
        {t('#portal.task.todo.form.button.label.close')}
      </CloseButton>
    </ControlWrapper>
  );
  const ReopenBtn = (
    <ControlWrapper className={classes.btnControlWrapper}>
      <ReopenButton
        onClick={disabled ? undefined : handleReopenTask}
        disabled={disabled}
      >
        {t('#portal.task.todo.form.button.label.reopen')}
      </ReopenButton>
    </ControlWrapper>
  );
  const CompleteBtn = (
    <ControlWrapper className={classes.btnControlWrapper}>
      <CompleteButton
        onClick={disabled ? undefined : handleCompleteTask}
        disabled={disabled}
      >
        {t('#portal.task.todo.form.button.label.complete')}
      </CompleteButton>
    </ControlWrapper>
  );
  const CancelBtn = (
    <ControlWrapper className={classes.btnControlWrapper}>
      <CancelButton
        onClick={disabled ? undefined : handleCancelTask}
        disabled={disabled}
      >
        {t('#portal.task.todo.form.button.label.cancel')}
      </CancelButton>
    </ControlWrapper>
  );

  return (
    <>
      <Stack
        direction={{
          md: 'row',
          xs: 'column',
        }}
        divider={<Divider orientation="vertical" flexItem />}
        spacing={2.5}
      >
        <Stack spacing={2.5} flex={1.5}>
          <Stack spacing={1}>
            <ControlLabel required>
              {t('#portal.task.todo.form.control.label.subject')}
            </ControlLabel>
            <ValidationWarning
              handleInitErrors={handleInitErrors}
              text={validationErrors.Subject}
              Component={
                <Input
                  value={values.Subject}
                  name="Subject"
                  onChange={handleChangeInput}
                  disabled={isDisabledField}
                />
              }
            />
          </Stack>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <ControlLabel>
                {t('#portal.task.todo.form.control.label.description')}
              </ControlLabel>
              <StyledIconButton onClick={handleOpenExpandedDescription}>
                <ExpandIcon sx={{ color: '#224060', fontSize: 18 }} />
              </StyledIconButton>
            </Stack>
            <ValidationWarning
              text={validationErrors.Description}
              handleInitErrors={handleInitErrors}
              Component={
                <Textarea
                  value={values.Description || ''}
                  name="Description"
                  placeholder={
                    isOwner && !isCompletedOrCanceledTask
                      ? 'Kirjoita kuvaus tehtävästä.'
                      : ''
                  }
                  onChange={handleChangeTextArea}
                  disabled={!isOwner || isCompletedOrCanceledTask}
                  className={classes.textArea}
                />
              }
            />
          </Stack>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <ControlLabel>
                {t(
                  '#portal.task.todo.form.control.label.resolutiondescription'
                )}
              </ControlLabel>
              <StyledIconButton
                onClick={handleOpenExpandedResolutionDescription}
              >
                <ExpandIcon sx={{ color: '#224060', fontSize: 18 }} />
              </StyledIconButton>
            </Stack>
            <ValidationWarning
              text={validationErrors.ResolutionDescription}
              handleInitErrors={handleInitErrors}
              Component={
                <Textarea
                  value={values.ResolutionDescription || ''}
                  name="ResolutionDescription"
                  placeholder={
                    isDisabledField ? '' : 'Kirjoita kuvaus ratkaisusta.'
                  }
                  onChange={handleChangeTextArea}
                  disabled={isDisabledField}
                  className={classes.textArea}
                />
              }
            />
          </Stack>
          <FileLoader
            documents={documents}
            entityId="task"
            caseId={taskId}
            editable={!isDisabledField}
            StackProps={{ direction: 'column-reverse' }}
            touchDocuments={touchDocuments}
          />
        </Stack>
        <Stack flex={1} gap={4}>
          <Stack
            spacing={1}
            sx={{
              order: { xs: 100, sm: 0 },
            }}
          >
            <ControlLabel>{t('Chat')}</ControlLabel>
            <Chat
              displayNotifyPMOnlyCheckbox
              readonly={isCompletedOrCanceledTask}
              entityId={taskId!}
              entityName={EntityName.Task}
            />
          </Stack>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack spacing={1}>
              <ControlLabel>
                {t('#portal.task.todo.form.control.label.startdate')}
              </ControlLabel>
              <DatePicker
                date={toDate(values.StartDate)}
                name="StartDate"
                handleChange={handleChangeDate}
                maxDate={toDate(values.DueDate)}
                disabled={isDisabledField}
                cls={datePickerCls}
              />
            </Stack>
            <Stack spacing={1}>
              <ControlLabel required>
                {t('#portal.task.todo.form.control.label.duedate')}
              </ControlLabel>
              <ValidationWarning
                text={validationErrors.DueDate}
                handleInitErrors={handleInitErrors}
                Component={
                  <DatePicker
                    date={toDate(values.DueDate)}
                    name="DueDate"
                    handleChange={handleChangeDate}
                    minDate={toDate(values.StartDate)}
                    disabled={isDisabledField}
                    cls={datePickerCls}
                  />
                }
              />
            </Stack>
          </Stack>

          <Stack spacing={1}>
            <ControlLabel>
              {t('#portal.task.todo.form.control.label.priority')}
            </ControlLabel>
            <Select
              value={values.Priority}
              options={options.Priority}
              name="Priority"
              onSelect={handleChangeSelect}
              labelField="Label"
              valueField="Value"
              disabled={isDisabledField}
              cls={selectCls}
              parentWidth
            />
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Stack spacing={1}>
              <ControlLabel>Omistaja</ControlLabel>
              <span className={classes.text}>{values.Owner?.Name}</span>
            </Stack>
            <Stack spacing={1}>
              <ControlLabel>Luotu</ControlLabel>
              <span className={classes.text}>
                {values.CreatedOn &&
                  customFormat(toDate(values.CreatedOn), 'datetime')}
              </span>
            </Stack>
          </Stack>
          <Stack spacing={1}>
            <ControlLabel required>
              {t('#portal.task.todo.form.control.label.executor')}
            </ControlLabel>
            <ValidationWarning
              text={validationErrors.Executor}
              handleInitErrors={handleInitErrors}
              Component={
                <Select
                  value={values.Executor}
                  options={options.Executor}
                  name="Executor"
                  onSelect={handleChangeSelect}
                  labelField="Name"
                  valueField="Id"
                  disabled={isDisabledField}
                  cls={selectCls}
                  parentWidth
                />
              }
            />
          </Stack>
          <Stack spacing={1}>
            <ControlLabel>
              {t('#portal.task.todo.form.control.label.status')}
            </ControlLabel>
            <Select
              value={values.Status}
              options={options.Status}
              name="Status"
              onSelect={handleChangeSelect}
              disabled={isDisabledField}
              labelField="Label"
              valueField="Value"
              cls={selectCls}
              parentWidth
            />
          </Stack>
        </Stack>
      </Stack>
      <div className={classes.buttonsWrapper}>
        <div className={classes.buttonsBox}>
          {notOwnerNorExecutor ? null : SaveBtn}
          {notOwnerNorExecutor ? null : SaveAndCopyBtn}
          {CloseBtn}
        </div>
        {notOwnerNorExecutor ? null : (
          <div className={classes.buttonsBox}>
            {isCompletedOrCanceledTask && ReopenBtn}
            {!isCompletedOrCanceledTask && CompleteBtn}
            {!isCompletedOrCanceledTask && CancelBtn}
          </div>
        )}
      </div>
      <Snackbar
        open={warningState.open}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={5000}
        onClose={handleCloseSubtaskWarning}
        TransitionProps={{
          onExited: handleInitWarningType,
        }}
      >
        <Alert elevation={6} variant="filled" severity="warning">
          {warningState.type ? Warning[warningState.type] : null}
        </Alert>
      </Snackbar>
      <SaveConfirm
        open={confirmationWarning}
        apply={closeWithSaving}
        cancel={closeWithoutSaving}
      />
      <Modal
        fullHeight
        fullWidth
        maxWidth="sm"
        open={isShowedExpandedDescription}
        handleClose={handleCloseExpandedDescription}
      >
        <ExpandedForm
          label={t('#portal.task.todo.form.control.label.description')}
          placeholder={
            isOwner && !isCompletedOrCanceledTask
              ? 'Kirjoita kuvaus tehtävästä.'
              : ''
          }
          value={values.Description || ''}
          disabled={!isOwner || isCompletedOrCanceledTask}
          onChange={onChangeDescription}
          onClose={handleCloseExpandedDescription}
        />
      </Modal>
      <Modal
        fullHeight
        fullWidth
        maxWidth="sm"
        open={isShowedExpandedResolutionDescription}
        handleClose={handleCloseExpandedResolutionDescription}
      >
        <ExpandedForm
          disabled={isDisabledField}
          label={t(
            '#portal.task.todo.form.control.label.resolutiondescription'
          )}
          placeholder={isDisabledField ? '' : 'Kirjoita kuvaus ratkaisusta.'}
          value={values.ResolutionDescription || ''}
          onChange={onChangeResolutionDescription}
          onClose={handleCloseExpandedResolutionDescription}
        />
      </Modal>
    </>
  );
};

export default TodoEditForm;
